// Client copy of backend/src/services/mapping_consistency.py — keep the two in
// step. The backend is the authority (it rejects a bad save and blocks a bad
// publish); this copy exists so the Map Payload editor can show problems as
// the user types, and so a field change can warn which connectors it breaks
// before it is saved.

const PLACEHOLDER = /\{\{(\w+)\}\}/g;
const EXACT = /^\{\{(\w+)\}\}$/;
const REQUEST_PLACEHOLDERS = new Set(["request_id"]);
const INDEX = "Index";
const ARRAY_TYPE = "array_of_objects";

/**
 * A blocked-delivery reason worded for a toast: "{{total}}" becomes "total"
 * and leftover JSON brackets are dropped. The toast treats text with { or [
 * as a raw technical error (summary + "Copy error"), which hid the field name.
 * The exact reason is still shown in full on the Exports tab.
 */
export const plainReason = (text) =>
  String(text || "")
    .replace(/\{\{(\w+)\}\}/g, '"$1"')
    .replace(/[{}[\]]/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

export const mappingTemplate = (payloadMapping) => {
  if (!payloadMapping || typeof payloadMapping !== "object") return null;
  if (payloadMapping.type !== "json_template") return null;
  const t = payloadMapping.template;
  return t && typeof t === "object" && !Array.isArray(t) && Object.keys(t).length
    ? t
    : null;
};

const parseColumns = (fieldValues) => {
  let v = fieldValues;
  if (typeof v === "string") {
    try {
      v = JSON.parse(v);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(v)) return [];
  return v
    .filter((c) => c && typeof c === "object" && typeof c.key === "string" && c.key)
    .map((c) => c.key);
};

export const describeTemplateFields = (fields) => {
  const out = {};
  for (const f of fields || []) {
    if (!f || f.is_active === false) continue;
    const name = f.field_name || f.name;
    if (!name) continue;
    const isArray = String(f.field_type || f.data_type || "text").toLowerCase() === ARRAY_TYPE;
    out[name] = { isArray, columns: isArray ? parseColumns(f.field_values) : [] };
  }
  return out;
};

const collect = (template) => {
  const refs = {
    scalars: new Set(),
    partial: new Set(),
    mappedArrays: new Set(),
    columns: {},
    rowConditions: {},
    misplacedIndex: false,
  };
  const colsOf = (table) => (refs.columns[table] ||= new Set());
  const isMap = (o) => o && typeof o === "object" && !Array.isArray(o) && "$$map" in o && "$$item" in o;

  const row = (obj, table) => {
    const cols = colsOf(table);
    if (isMap(obj)) {
      if (typeof obj.$$map === "string") cols.add(obj.$$map);
      return;
    }
    if (Array.isArray(obj)) {
      for (let item of obj) {
        if (item && typeof item === "object" && !Array.isArray(item) && "$$if" in item) {
          if (typeof item.$$if === "string") {
            (refs.rowConditions[table] ||= new Set()).add(item.$$if);
          }
          const { $$if, ...rest } = item; // eslint-disable-line no-unused-vars
          item = rest;
        }
        row(item, table);
      }
      return;
    }
    if (obj && typeof obj === "object") {
      Object.values(obj).forEach((v) => row(v, table));
      return;
    }
    if (typeof obj === "string") {
      for (const m of obj.matchAll(PLACEHOLDER)) {
        if (m[1] !== INDEX) cols.add(m[1]);
      }
    }
  };

  const top = (obj) => {
    if (isMap(obj)) {
      if (typeof obj.$$map === "string") {
        refs.mappedArrays.add(obj.$$map);
        colsOf(obj.$$map);
        row(obj.$$item, obj.$$map);
      }
      return;
    }
    if (Array.isArray(obj)) {
      for (let item of obj) {
        if (item && typeof item === "object" && !Array.isArray(item) && "$$if" in item) {
          if (typeof item.$$if === "string") refs.scalars.add(item.$$if);
          const { $$if, ...rest } = item; // eslint-disable-line no-unused-vars
          item = rest;
        }
        top(item);
      }
      return;
    }
    if (obj && typeof obj === "object") {
      for (const [k, v] of Object.entries(obj)) {
        if (k !== "$$omit_unless_date" && k !== "$$omit_if_empty") top(v);
      }
      return;
    }
    if (typeof obj === "string") {
      const exact = obj.trim().match(EXACT);
      if (exact) {
        if (exact[1] === INDEX) refs.misplacedIndex = true;
        else refs.scalars.add(exact[1]);
        return;
      }
      for (const m of obj.matchAll(PLACEHOLDER)) {
        if (m[1] === INDEX) refs.misplacedIndex = true;
        else refs.partial.add(m[1]);
      }
    }
  };

  top(template);
  return refs;
};

const problem = (code, field, message) => ({ code, field, message });

/**
 * Compare one link's Map Payload with its template. Same result shape as the
 * backend check_mapping (minus the request-level check, which only applies at
 * publish time).
 */
export const checkMapping = (payloadMapping, templateFields) => {
  const tmpl = mappingTemplate(payloadMapping);
  if (!tmpl) {
    return { checked: false, status: "ok", errors: [], unused_fields: [], excluded_fields: [] };
  }
  const fields = describeTemplateFields(templateFields);
  const refs = collect(tmpl);
  const excluded = new Set((payloadMapping.excluded_fields || []).filter((e) => typeof e === "string"));
  const errors = [];
  const known = (n) => n in fields || REQUEST_PLACEHOLDERS.has(n);
  const sorted = (s) => [...s].sort();

  for (const name of sorted(new Set([...refs.scalars, ...refs.partial]))) {
    if (!known(name)) errors.push(problem("unknown_field", name, `{{${name}}} is not a field on this template`));
  }
  for (const name of sorted(refs.partial)) {
    if (fields[name]?.isArray) {
      errors.push(problem("array_in_text", name, `"${name}" is a table field and cannot be placed inside text; use {"$$map": "${name}", "$$item": {...}}`));
    }
  }
  for (const table of sorted(refs.mappedArrays)) {
    if (!(table in fields)) {
      errors.push(problem("unknown_field", table, `"$$map": "${table}" — "${table}" is not a field on this template`));
    } else if (!fields[table].isArray) {
      errors.push(problem("not_an_array", table, `"$$map": "${table}" — "${table}" is a single-value field, not a table`));
    }
  }
  for (const table of Object.keys(refs.columns).sort()) {
    const info = fields[table];
    if (!info?.isArray) continue;
    for (const col of sorted(refs.columns[table])) {
      if (!info.columns.includes(col)) {
        errors.push(problem("unknown_column", `${table}.${col}`, `{{${col}}} inside the "${table}" $$item is not a column of "${table}"`));
      }
    }
  }
  for (const table of Object.keys(refs.rowConditions).sort()) {
    const info = fields[table];
    if (!info?.isArray) continue;
    for (const name of sorted(refs.rowConditions[table])) {
      if (!info.columns.includes(name) && !(name in fields)) {
        errors.push(problem("unknown_field", `${table}.${name}`, `"$$if": "${name}" inside the "${table}" $$item is neither a column of "${table}" nor a template field`));
      }
    }
  }
  if (refs.misplacedIndex) {
    errors.push(problem("misplaced_index", INDEX, "{{Index}} only works inside a $$item block"));
  }

  const usedTop = new Set(
    [...refs.scalars, ...refs.partial, ...refs.mappedArrays].filter((n) => n in fields),
  );
  Object.values(refs.rowConditions).forEach((s) => s.forEach((n) => n in fields && usedTop.add(n)));

  const unused = [];
  for (const name of Object.keys(fields).sort()) {
    const info = fields[name];
    if (excluded.has(name)) continue;
    if (!usedTop.has(name)) {
      unused.push(name);
      continue;
    }
    if (info.isArray && refs.mappedArrays.has(name) && !refs.scalars.has(name)) {
      const usedCols = new Set([...(refs.columns[name] || []), ...(refs.rowConditions[name] || [])]);
      for (const col of info.columns) {
        const key = `${name}.${col}`;
        if (!usedCols.has(col) && !excluded.has(key)) unused.push(key);
      }
    }
  }
  for (const key of unused) {
    errors.push(
      problem("unmapped_field", key, `"${key}" is on the template but not sent to this connector — map it or mark it "not sent"`),
    );
  }

  const stillExcluded = [...excluded]
    .filter((e) => {
      if (e in fields) return true;
      const [t, c] = e.split(".", 2);
      return c !== undefined && fields[t]?.columns.includes(c);
    })
    .sort();

  return {
    checked: true,
    status: errors.length ? "error" : "ok",
    errors,
    unused_fields: unused,
    excluded_fields: stillExcluded,
  };
};

/**
 * Connectors a proposed field change would newly put out of sync. Problems a
 * link already had before the change are not repeated — only what this change
 * causes.
 */
export const mappingImpact = (linkedWebhooks, currentFields, nextFields) => {
  const out = [];
  for (const wh of linkedWebhooks || []) {
    if (!mappingTemplate(wh.payload_mapping)) continue;
    const before = new Set(checkMapping(wh.payload_mapping, currentFields).errors.map((e) => `${e.code}:${e.field}`));
    const added = checkMapping(wh.payload_mapping, nextFields).errors.filter(
      (e) => !before.has(`${e.code}:${e.field}`),
    );
    if (added.length) out.push({ webhook_id: wh.id, name: wh.name, errors: added });
  }
  return out;
};

/**
 * Per template field, the connectors it doesn't reach — for the Fields tab, so
 * the template shows the same differences the Map Payload editor does.
 *
 * Returns { [field_name]: [{ webhook_id, name, kind, columns }] } where kind is
 * "excluded" (marked "not sent") or "unused" (neither sent nor marked — an
 * error that blocks publishing to that connector). columns is empty when the whole field is left out,
 * or lists the table columns left out when only some are.
 */
export const fieldDeliveryNotes = (linkedWebhooks, templateFields) => {
  const notes = {};
  const add = (key, wh, kind) => {
    const [field, column] = key.split(".", 2);
    const list = (notes[field] = notes[field] || []);
    let entry = list.find((n) => n.webhook_id === wh.id && n.kind === kind);
    if (!entry) {
      entry = { webhook_id: wh.id, name: wh.name, kind, columns: [] };
      list.push(entry);
    }
    if (column) entry.columns.push(column);
  };
  for (const wh of linkedWebhooks || []) {
    const result = checkMapping(wh.payload_mapping, templateFields);
    if (!result.checked) continue;
    result.excluded_fields.forEach((key) => add(key, wh, "excluded"));
    result.unused_fields.forEach((key) => add(key, wh, "unused"));
  }
  return notes;
};
